import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { toast } from "react-toastify";
import { useParams, useNavigate } from "react-router-dom";
import {
  crearProveedor,
  crearPropuesta,
  obtenerProveedorPorCedula,
} from "../services/solicitudesService";

/* ================= Helpers de normalización ================= */

const toNumber = (v) => {
  if (v === null || v === undefined) return NaN;
  if (typeof v === "number") return v;

  const s = String(v)
    .trim()
    .replace(/\s+/g, "")
    // quitar punto de miles (2.260 -> 2260) cuando va entre miles
    .replace(/(?<=\d)[.](?=\d{3}(\D|$))/g, "")
    // convertir coma decimal a punto (260,00 -> 260.00)
    .replace(/,/, ".");

  const n = Number(s);
  return Number.isFinite(n) ? n : NaN;
};

const toInt = (v) => {
  const n = toNumber(v);
  return Number.isFinite(n) ? Math.trunc(n) : NaN;
};

const mesMap = {
  enero: 1, febrero: 2, marzo: 3, abril: 4, mayo: 5, junio: 6,
  julio: 7, agosto: 8, setiembre: 9, septiembre: 9, octubre: 10,
  noviembre: 11, diciembre: 12, jan: 1, feb: 2, mar: 3, apr: 4,
  may: 5, jun: 6, jul: 7, aug: 8, sep: 9, oct: 10, nov: 11, dec: 12,
};

const normalizarFila = (p) => {
  const mesTxt = (p.mes ?? "").toString().trim().toLowerCase();
  const mes = mesMap[mesTxt] !== undefined ? mesMap[mesTxt] : toInt(p.mes);

  const monto = toNumber(p.monto);
  const ivaXlsx = toNumber(p.iva);
  const totalXlsx = toNumber(p.total);

  const iva = Number.isFinite(ivaXlsx)
    ? ivaXlsx
    : Number.isFinite(monto)
    ? +(monto * 0.13).toFixed(2)
    : NaN;

  const total =
    Number.isFinite(totalXlsx) ? totalXlsx :
    (Number.isFinite(monto) && Number.isFinite(iva)) ? +(monto + iva).toFixed(2) : NaN;

  return {
    ...p,
    mes,
    ano: toInt(p.ano),
    horas: toNumber(p.horas),
    monto,
    iva,
    total,
    porcentajeCobro: toNumber(p.porcentajeCobro),
    observaciones: (p.observaciones ?? "").toString().trim(),
  };
};

/* ================= Componente ================= */

export default function ParticiparSolicitud() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [form, setForm] = useState({
    cedula: "",
    nombreProveedor: "",
    representante: "",
    telefono: "",
    correo: "",
    desglose: JSON.parse(localStorage.getItem("desgloseExcel") || "[]"),
  });

  const [proveedorExiste, setProveedorExiste] = useState(false);
  const [datosProveedor, setDatosProveedor] = useState({
    representante: "",
    telefono: "",
    correo: "",
  });

  const [propuestas, setPropuestas] = useState([
    { mes: "", ano: "", horas: "", monto: "", iva: "", total: "", observaciones: "", porcentajeCobro: "" },
  ]);

  useEffect(() => {
    // Recuperar desglose Excel
    const desgloseExcel = JSON.parse(localStorage.getItem("desgloseExcel") || "[]");
    if (desgloseExcel.length > 0) {
      const mapped = desgloseExcel.map((d) =>
        normalizarFila({
          mes: d.Mes ?? d.mes ?? "",
          ano: d.Año ?? d.ano ?? "",
          horas: d.Horas ?? d.horas ?? "",
          monto: d.Monto ?? d.monto ?? "",
          iva: d.IVA ?? d.iva ?? "",
          total: d.Total ?? d.total ?? "",
          observaciones: d.Observaciones ?? d.observaciones ?? "",
          porcentajeCobro: d["Porcentaje de cobro"] ?? d.porcentajeCobro ?? "",
        })
      );
      setPropuestas(mapped);
      localStorage.removeItem("desgloseExcel");
    }
    // Recuperar datos del proveedor si existen
    const proveedorLS = JSON.parse(localStorage.getItem("datosProveedorExcel") || "null");
    if (proveedorLS) {
      setForm((f) => ({
        ...f,
        cedula: proveedorLS.cedula || "",
        nombreProveedor: proveedorLS.nombreProveedor || "",
      }));
      setDatosProveedor({
        representante: proveedorLS.representante || "",
        telefono: proveedorLS.telefono || "",
        correo: proveedorLS.correo || "",
      });
      // Marcar proveedorExiste como true si la cédula es válida y hay datos
      if (proveedorLS.cedula && proveedorLS.nombreProveedor) {
        setProveedorExiste(true);
      }
      localStorage.removeItem("datosProveedorExcel");
    }
  }, []);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  const validarCedula = (cedula) => /^[\w-]+$/.test(cedula);

  const handleCedulaChange = async (e) => {
    const cedula = e.target.value;
    setForm((f) => ({ ...f, cedula }));

    if (!validarCedula(cedula)) {
      setProveedorExiste(false);
      setForm((f) => ({ ...f, nombreProveedor: "" }));
      setDatosProveedor({ representante: "", telefono: "", correo: "" });
      return;
    }

    try {
      const proveedor = await obtenerProveedorPorCedula(cedula);
      if (proveedor && proveedor.cedulaJuridica) {
        setProveedorExiste(true);
        setForm((f) => ({ ...f, nombreProveedor: proveedor.nombreProveedor || "" }));
        setDatosProveedor({
          representante: proveedor.nombreRepresentante || "",
          telefono: proveedor.telefono || "",
          correo: proveedor.correoElectronico || "",
        });
      } else {
        setProveedorExiste(false);
        setForm((f) => ({ ...f, nombreProveedor: "" }));
        setDatosProveedor({ representante: "", telefono: "", correo: "" });
      }
    } catch {
      setProveedorExiste(false);
      setForm((f) => ({ ...f, nombreProveedor: "" }));
      setDatosProveedor({ representante: "", telefono: "", correo: "" });
    }
  };

const handleConfirmar = async () => {
  if (
    !form.cedula ||
    !form.nombreProveedor ||
    propuestas.some(
      (p) =>
        p.mes === "" ||
        p.ano === "" ||
        p.horas === "" ||
        p.monto === "" ||
        p.porcentajeCobro === ""
    )
  ) {
    setError("Todos los datos son requeridos.");
    toast.error("Todos los datos son requeridos.", { autoClose: 3000 });
    return;
  }

  const correoValido = proveedorExiste
    ? true
    : /^[\w-.]+@([\w-]+\.)+[\w-]{2,4}$/.test(datosProveedor.correo);
  if (!correoValido) {
    setError("El correo electrónico no tiene un formato válido.");
    toast.error("El correo electrónico no tiene un formato válido.", { autoClose: 3000 });
    return;
  }
  const telefonoValido = proveedorExiste ? true : /^\d{8,}$/.test(datosProveedor.telefono);
  if (!telefonoValido) {
    setError("El teléfono debe ser numérico y tener al menos 8 dígitos.");
    toast.error("El teléfono debe ser numérico y tener al menos 8 dígitos.", { autoClose: 3000 });
    return;
  }

  setLoading(true);
  setError(null);
  setSuccess(null);

  // helpers locales
  const toNumber = (v) => {
    if (v === null || v === undefined) return NaN;
    if (typeof v === "number") return v;
    const s = String(v)
      .trim()
      .replace(/\s+/g, "")
      .replace(/(?<=\d)[.](?=\d{3}(\D|$))/g, "")
      .replace(/,/, ".");
    const n = Number(s);
    return Number.isFinite(n) ? n : NaN;
  };
  const toInt = (v) => {
    const n = toNumber(v);
    return Number.isFinite(n) ? Math.trunc(n) : NaN;
  };

  // mapa meses: 1 -> 'enero', etc.
  const meses = ["enero","febrero","marzo","abril","mayo","junio","julio","agosto","setiembre","octubre","noviembre","diciembre"];
  const mesFromInput = (m) => {
    // Si ya viene texto, lo dejamos en minúsculas
    if (isNaN(Number(m))) return String(m).trim().toLowerCase();
    // Si viene número 1..12 lo convertimos a nombre
    const num = toInt(m);
    return meses[num - 1] ?? "";
  };

  try {
    if (!proveedorExiste) {
      await crearProveedor({
        cedulaJuridica: form.cedula,
        nombreProveedor: form.nombreProveedor,
        nombreRepresentante: datosProveedor.representante,
        telefono: datosProveedor.telefono,
        correoElectronico: datosProveedor.correo,
      });
    }

    for (let i = 0; i < propuestas.length; i++) {
      const raw = propuestas[i];

      const mesTexto = mesFromInput(raw.mes); // <-- API espera nombre del mes
      const ano = toInt(raw.ano);
      const horas = toInt(raw.horas);         // entero
      const monto = toNumber(raw.monto);
      let iva = toNumber(raw.iva);
      let total = toNumber(raw.total);
      const porcentaje = toNumber(raw.porcentajeCobro); // <-- 0..100 (no dividir)

      // Recalcular IVA/Total si hace falta
      if (Number.isFinite(monto)) {
        if (!Number.isFinite(iva)) iva = +(monto * 0.13).toFixed(2);
        if (!Number.isFinite(total)) total = +(monto + iva).toFixed(2);
      }

      // Validaciones previas
      if (!mesTexto || !Number.isFinite(ano)) {
        const d = `Fila ${i + 1} - Mes: ${raw.mes}, Año: ${raw.ano}`;
        toast.error(`Error: Mes/Año inválidos en ${d}`, { autoClose: 6000 });
        setLoading(false); return;
      }
      if (!Number.isFinite(horas)) {
        const d = `Fila ${i + 1} - Horas: ${raw.horas}`;
        toast.error(`Error: Horas debe ser entero. ${d}`, { autoClose: 6000 });
        setLoading(false); return;
      }
      if (!Number.isFinite(monto) || !Number.isFinite(iva) || !Number.isFinite(total)) {
        const d = `Fila ${i + 1} - Monto/IVA/Total inválidos`;
        toast.error(`Error: ${d}`, { autoClose: 6000 });
        setLoading(false); return;
      }
      if (!Number.isFinite(porcentaje) || porcentaje < 0 || porcentaje > 100) {
        const d = `Fila ${i + 1} - Porcentaje de cobro: ${raw.porcentajeCobro}`;
        toast.error(`Error: Porcentaje debe estar entre 0 y 100. ${d}`, { autoClose: 6000 });
        setLoading(false); return;
      }

      const payload = {
        consecutivoOficioSolicitud: String(id), // <-- como string (igual que en Postman)
        cedulaProveedor: form.cedula,
        mes: mesTexto,                           // <-- nombre del mes
        ano,
        horasInvertidas: horas,
        monto,
        iva,
        total,
        porcentaje,                              // <-- 0..100
        observaciones: (raw.observaciones ?? "").toString().trim(),
      };

      console.log("POST crearPropuesta payload:", payload);

      try {
        await crearPropuesta(payload);
      } catch (err) {
        const serverMsg =
          err?.response?.data?.message ??
          err?.response?.data?.detail ??
          err?.response?.data?.error ??
          err?.message ??
          "Error al crear propuesta";
        const status = err?.response?.status;
        console.error("crearPropuesta ERROR", status, err?.response?.data, err);

        const d = `Fila ${i + 1} - Mes: ${raw.mes}, Año: ${raw.ano}, Horas: ${raw.horas}, Monto: ${raw.monto}`;
        setError(`Error en ${d}: ${serverMsg}`);
        toast.error(`Error en ${d}: ${serverMsg}`, { autoClose: 6000 });
        setLoading(false);
        return; // salir y no mostrar success
      }
    }

    setSuccess("Propuestas enviadas correctamente.");
    toast.success("Propuestas enviadas correctamente.", { autoClose: 2000 });
  } catch (err) {
    setError(err.message);
    toast.error("Error al enviar las propuestas", { autoClose: 3000 });
  } finally {
    setLoading(false);
  }
};


  return (
    <motion.div className="container my-5" initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
      <h2 className="mb-4 text-primary">Propuesta a la Solicitud</h2>

      <form className="row g-3">
        <div className="col-md-6">
          <label className="form-label">Cédula jurídica del proveedor:</label>
          <input className="form-control" value={form.cedula} onChange={handleCedulaChange} />
          {!validarCedula(form.cedula) && form.cedula && <span className="text-danger">Formato inválido</span>}
          {proveedorExiste && <span className="text-success">Proveedor encontrado.</span>}
        </div>

        <div className="col-md-6">
          <label className="form-label">Nombre del proveedor:</label>
          <input
            className="form-control"
            value={form.nombreProveedor}
            disabled={proveedorExiste}
            onChange={(e) => setForm({ ...form, nombreProveedor: e.target.value })}
          />
        </div>

        {/* Datos adicionales si el proveedor no existe */}
        {!proveedorExiste ? (
          <div className="col-12">
            <div className="row">
              <div className="col-md-4">
                <label className="form-label">Nombre del representante:</label>
                <input
                  className="form-control"
                  value={datosProveedor.representante}
                  onChange={(e) => setDatosProveedor({ ...datosProveedor, representante: e.target.value })}
                />
              </div>
              <div className="col-md-4">
                <label className="form-label">Teléfono:</label>
                <input
                  className="form-control"
                  value={datosProveedor.telefono}
                  onChange={(e) => setDatosProveedor({ ...datosProveedor, telefono: e.target.value })}
                />
              </div>
              <div className="col-md-4">
                <label className="form-label">Correo electrónico:</label>
                <input
                  className="form-control"
                  value={datosProveedor.correo}
                  onChange={(e) => setDatosProveedor({ ...datosProveedor, correo: e.target.value })}
                />
              </div>
            </div>
          </div>
        ) : (
          <div className="col-12">
            <div className="row">
              <div className="col-md-4">
                <label className="form-label">Nombre del representante:</label>
                <input className="form-control" value={datosProveedor.representante || "<No indicado>"} disabled />
              </div>
              <div className="col-md-4">
                <label className="form-label">Teléfono:</label>
                <input className="form-control" value={datosProveedor.telefono || "<No indicado>"} disabled />
              </div>
              <div className="col-md-4">
                <label className="form-label">Correo electrónico:</label>
                <input className="form-control" value={datosProveedor.correo || "<No indicado>"} disabled />
              </div>
            </div>
          </div>
        )}

        {/* Desglose de propuestas */}
        <div className="col-12">
          <h5>Detalle de las propuestas</h5>

          {propuestas.map((p, idx) => (
            <div key={idx} className="border rounded p-3 mb-3">
              <div className="row">
                <div className="col-md-2">
                  <label className="form-label">Mes:</label>
                  <input
                    className="form-control"
                    value={p.mes}
                    onChange={(e) => {
                      const val = e.target.value;
                      setPropuestas((arr) => arr.map((x, i) => (i === idx ? { ...x, mes: val } : x)));
                    }}
                  />
                </div>
                <div className="col-md-2">
                  <label className="form-label">Año:</label>
                  <input
                    className="form-control"
                    value={p.ano}
                    onChange={(e) => {
                      const val = e.target.value;
                      setPropuestas((arr) => arr.map((x, i) => (i === idx ? { ...x, ano: val } : x)));
                    }}
                  />
                </div>
                <div className="col-md-2">
                  <label className="form-label">Horas:</label>
                  <input
                    className="form-control"
                    value={p.horas}
                    onChange={(e) => {
                      const val = e.target.value;
                      setPropuestas((arr) => arr.map((x, i) => (i === idx ? { ...x, horas: val } : x)));
                    }}
                  />
                </div>

                {/* Recalcular con número normalizado */}
                <div className="col-md-2">
                  <label className="form-label">Monto:</label>
                  <input
                    className="form-control"
                    value={p.monto}
                    onChange={(e) => {
                      const val = e.target.value;
                      const monto = toNumber(val);
                      const iva = Number.isFinite(monto) ? (monto * 0.13).toFixed(2) : "";
                      const total = Number.isFinite(monto) ? (monto + Number(iva)).toFixed(2) : "";
                      setPropuestas((arr) => arr.map((x, i) => (i === idx ? { ...x, monto: val, iva, total } : x)));
                    }}
                  />
                </div>
                <div className="col-md-2">
                  <label className="form-label">IVA (13%):</label>
                  <input
                    className="form-control"
                    style={{ fontSize: "1.2rem", fontWeight: "bold", background: "#e3f2fd" }}
                    value={p.iva}
                    readOnly
                  />
                </div>
                <div className="col-md-2">
                  <label className="form-label">Total:</label>
                  <input
                    className="form-control"
                    style={{ fontSize: "1.2rem", fontWeight: "bold", background: "#e3f2fd" }}
                    value={p.total}
                    readOnly
                  />
                </div>
              </div>

              <div className="row mt-2">
                <div className="col-md-8">
                  <label className="form-label">Observaciones:</label>
                  <input
                    className="form-control"
                    value={p.observaciones}
                    onChange={(e) => {
                      const val = e.target.value;
                      setPropuestas((arr) => arr.map((x, i) => (i === idx ? { ...x, observaciones: val } : x)));
                    }}
                  />
                </div>
                <div className="col-md-4">
                  <label className="form-label">Porcentaje de cobro:</label>
                  <input
                    className="form-control"
                    value={p.porcentajeCobro}
                    onChange={(e) => {
                      const val = e.target.value;
                      setPropuestas((arr) => arr.map((x, i) => (i === idx ? { ...x, porcentajeCobro: val } : x)));
                    }}
                  />
                </div>
              </div>
            </div>
          ))}

          <button
            type="button"
            className="btn btn-dark mb-3"
            onClick={() =>
              setPropuestas([
                ...propuestas,
                { mes: "", ano: "", horas: "", monto: "", iva: "", total: "", observaciones: "", porcentajeCobro: "" },
              ])
            }
          >
            + Agregar otra propuesta
          </button>
        </div>

        <div className="col-12 mt-3 d-flex justify-content-between">
          <button
            className="btn btn-success"
            type="button"
            onClick={() => {
              // Guardar datos del proveedor antes de navegar
              localStorage.setItem(
                "datosProveedorExcel",
                JSON.stringify({
                  cedula: form.cedula,
                  nombreProveedor: form.nombreProveedor,
                  representante: datosProveedor.representante,
                  telefono: datosProveedor.telefono,
                  correo: datosProveedor.correo,
                })
              );
              navigate(`/cargar-excel/${id}`);
            }}
          >
            Cargar Excel
          </button>
          <button className="btn btn-secondary" type="button" onClick={() => navigate(-1)}>
            Cancelar
          </button>
          <button className="btn btn-primary" type="button" onClick={handleConfirmar} disabled={loading}>
            Confirmar
          </button>
        </div>
      </form>

      {loading && <div className="alert alert-info mt-3">Enviando...</div>}
      {error && <div className="alert alert-danger mt-3">{error}</div>}
      {success && <div className="alert alert-success mt-3">{success}</div>}
    </motion.div>
  );
}
